import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import SimpleLoader from '../../../common/SimpleLoader';
import {showToast} from "../../../../../utilities/helpers/ToastHelper";
import * as Sentry from '@sentry/react';


const GenericProviderFieldDialog = ({
    dialogTitle,
    initialValue,
    label,
    saveButtonText,
    onUpdate,
    setDialogVisibility,
    onValueUpdate,
    validate,
    toastPosition = 'bottom-left',
    allowEmpty = false
}) => {
    const [opacity, setOpacity] = useState(0);
    const [fieldValue, setFieldValue] = useState(initialValue);
    const [showLoader, setLoaderVisibility] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, [])

    const handleUpdate = async () => {
        // Basic empty validation
        if (!allowEmpty && (!fieldValue || !fieldValue.trim())) {
            showToast({
                type: 'error',
                message: "Field cannot be empty.",
                position: toastPosition,
                className: 'toast-white-error'
            });
            return;
        }

        // If custom validate is provided, apply it
        if (validate) {
            const customError = validate(fieldValue);
            if (customError) {
                showToast({
                    type: 'error',
                    message: customError,
                    position: toastPosition,
                    className: 'toast-white-error'
                });
                return;
            }
        }

        try {
            if (isMounted.current) setLoaderVisibility(true); // Only set loader if still mounted
            setLoaderVisibility(true);
            await onUpdate(fieldValue);

            if(isMounted.current) {
                showToast({
                    type: 'success',
                    message: "Update successful!",
                });

                onValueUpdate && onValueUpdate(fieldValue);
                onCancelClicked();
            }
        } catch (error) {
            Sentry.captureException(error);
            let errorMessage = "Server error!";
            if (error?.response?.data?.message) {
                errorMessage += ": " + error.response.data.message;
            }
            if(isMounted.current){
                showToast({
                    type: 'error',
                    message: errorMessage,
                    position: toastPosition,
                    className: 'toast-white-error'
                });
            }
        } finally {
            if(isMounted.current){
                setLoaderVisibility(false);
            }
        }
    };

    const onCancelClicked = () => {
        setOpacity(0);
        setTimeout(() => {
            setDialogVisibility(false);
        }, 800);
    };

    useEffect(() => {
        setTimeout(() => setOpacity(1), 50);
    }, []);

    return (
        <div className="dialog-wrapper" style={{ opacity }}>
            <div className="share-dialog-container share-dialog-container-commission" data-testid="share-dialog">
                <div className="share-dialog-content-container">
                    <div className="share-dialog-header">
                        <div className="login-logo" style={{ marginTop: 0 }} />
                        <div
                            onClick={onCancelClicked}
                            className="dialog-close-button"
                            style={{ position: 'unset' }}
                        />
                    </div>
                    <div className="flex-filler" />
                    <h3 style={{ fontStyle: 'normal', marginTop: 40, marginBottom: 16 }}>
                        {dialogTitle}
                    </h3>
                    <div className="share-link-header">
                        <label style={{ marginRight: 8 }}>{label}:</label>
                        <input
                            type="text"
                            onChange={e => setFieldValue(e.target.value)}
                            value={fieldValue}
                            className="form-control-1"
                        />
                        <div
                            className="secondary-button"
                            style={{ marginLeft: 10, fontFamily: 'Roboto' }}
                            onClick={handleUpdate}
                        >
                            {saveButtonText}
                        </div>
                    </div>
                </div>
            </div>
            <SimpleLoader showLoader={showLoader} />
        </div>
    );
};

GenericProviderFieldDialog.propTypes = {
    dialogTitle: PropTypes.string.isRequired,
    initialValue: PropTypes.any,
    label: PropTypes.string.isRequired,
    saveButtonText: PropTypes.string,
    onUpdate: PropTypes.func.isRequired,                // async function (newValue: string) => Promise
    setDialogVisibility: PropTypes.func.isRequired,
    onValueUpdate: PropTypes.func,                       // Optional: called with new value after successful update,
    validate: PropTypes.func,    // validation function: (value: string) => error message or null
    toastPosition: PropTypes.string,
    allowEmpty: PropTypes.bool
};

GenericProviderFieldDialog.defaultProps = {
    saveButtonText: 'Save',
    toastPosition: "bottom-left",
    allowEmpty: false
};

export default GenericProviderFieldDialog;